/**
 * Data Point Entity (Simplified)
 * Just neutral data points - they shape the regression line
 * No good/bad distinction - all points are just DATA
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';

export class DataPoint {
    constructor(scene, x, y) {
        this.scene = scene;
        this.destroyed = false;

        // Position
        this.x = x;
        this.y = y;

        // Visual - use custom image (1080px image scaled to ~24px)
        this.sprite = scene.add.image(x, y, 'datapoint');
        this.sprite.setDepth(40);
        this.sprite.setScale(24 / 1080);  // Scale to ~24px

        // Size for collision
        this.radius = 12;

        // Float animation
        this.floatOffset = Math.random() * Math.PI * 2;
        this.baseY = y;
    }

    update(delta, cameraX) {
        if (this.destroyed) return;

        // Subtle float animation
        const float = Math.sin(this.scene.time.now * 0.002 + this.floatOffset) * 2;
        this.y = this.baseY + float;

        // Update sprite position
        this.sprite.setPosition(this.x, this.y);
    }

    isOffScreen(cameraX) {
        // Point is off screen when it's behind the camera
        return this.x < cameraX - 50;
    }

    // Check if point is visible on screen (for regression calculation)
    isOnScreen(cameraX) {
        return this.x >= cameraX && this.x <= cameraX + this.scene.scale.width + 100;
    }

    // Check collision with player (circle vs circle)
    checkCollision(playerX, playerY, playerRadius) {
        if (this.destroyed) return false;

        const dx = playerX - this.x;
        const dy = playerY - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        return distance < (this.radius + playerRadius);
    }

    destroy() {
        this.destroyed = true;
        if (this.sprite) this.sprite.destroy();
    }
}

/**
 * Data Point Manager (Simplified)
 * Spawns data points - regression line fits to VISIBLE points only
 */
export class DataPointManager {
    constructor(scene, regressionLine) {
        this.scene = scene;
        this.regressionLine = regressionLine;

        this.dataPoints = [];
        this.spawnTimer = 0;
        this.spawnInterval = CONSTANTS.DATA_POINT_SPAWN_INTERVAL;

        // Camera tracking
        this.cameraX = 0;

        // Difficulty affects spread
        this.difficultyMultiplier = 1.0;
        this.timeSurvived = 0;
    }

    update(delta, player, score, cameraX) {
        this.cameraX = cameraX;
        this.timeSurvived += delta;

        // Gradually increase difficulty
        this.difficultyMultiplier = 1 + (this.timeSurvived / 30000) * 0.5; // +50% spread over 30 seconds

        // Spawn timer
        this.spawnTimer += delta;

        if (this.spawnTimer >= this.spawnInterval) {
            this.spawnDataPoint();
            this.spawnTimer = 0;
        }

        // Update all data points
        for (const dp of this.dataPoints) {
            dp.update(delta, cameraX);
        }

        // Remove off-screen points (behind camera)
        this.dataPoints = this.dataPoints.filter(dp => {
            if (dp.destroyed || dp.isOffScreen(cameraX)) {
                if (!dp.destroyed) dp.destroy();
                return false;
            }
            return true;
        });

        // Check for player collisions (OVERFITTING!)
        const collisionResult = this.checkCollisions(player);

        // Update regression line based on VISIBLE points only
        this.updateRegressionLine();

        return collisionResult;
    }

    checkCollisions(player) {
        const playerRadius = player.getCollisionRadius();

        for (const dp of this.dataPoints) {
            if (dp.checkCollision(player.x, player.y, playerRadius)) {
                // Player touched a data point = OVERFITTING!
                dp.destroy();
                return { hit: true, type: 'datapoint', growth: CONSTANTS.GROWTH_DATAPOINT };
            }
        }
        return { hit: false };
    }

    spawnDataPoint() {
        // Spawn at right edge of screen (just coming into view)
        const spawnX = this.cameraX + this.scene.scale.width + Math.random() * 50;

        // Random Y position (scattered data!)
        const centerY = this.scene.scale.height / 2;
        let spread = CONSTANTS.DATA_POINT_BASE_SPREAD + this.difficultyMultiplier * CONSTANTS.DATA_POINT_DIFFICULTY_SPREAD;

        // Chance of extreme outlier (double spread)
        if (Math.random() < CONSTANTS.DATA_POINT_OUTLIER_CHANCE) {
            spread *= 2;
        }

        const spawnY = centerY + (Math.random() - 0.5) * spread;

        // Clamp to screen bounds
        const clampedY = Math.max(80, Math.min(this.scene.scale.height - 80, spawnY));

        const dataPoint = new DataPoint(this.scene, spawnX, clampedY);
        this.dataPoints.push(dataPoint);
    }

    updateRegressionLine() {
        // Get only points that are ON SCREEN or BEHIND camera (not ahead)
        const visiblePoints = this.dataPoints.filter(dp =>
            dp.x <= this.cameraX + this.scene.scale.width && !dp.destroyed
        );

        if (visiblePoints.length < 2) return;

        // Calculate linear regression: y = mx + b
        // Using least squares method
        const n = visiblePoints.length;
        let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

        for (const dp of visiblePoints) {
            // Use position relative to camera for stable calculation
            const relX = dp.x - this.cameraX;
            sumX += relX;
            sumY += dp.y;
            sumXY += relX * dp.y;
            sumX2 += relX * relX;
        }

        const denominator = n * sumX2 - sumX * sumX;
        if (Math.abs(denominator) < 0.001) return;

        const slope = (n * sumXY - sumX * sumY) / denominator;
        const intercept = (sumY - slope * sumX) / n;

        // Update regression line smoothly
        this.regressionLine.setTargetRegression(slope, intercept);
    }

    getDifficultyMultiplier() {
        return this.difficultyMultiplier;
    }

    getVisiblePointCount(cameraX) {
        // Count points that are on screen or behind camera (used for regression)
        return this.dataPoints.filter(dp =>
            dp.x <= cameraX + this.scene.scale.width && !dp.destroyed
        ).length;
    }

    clear() {
        this.dataPoints.forEach(dp => dp.destroy());
        this.dataPoints = [];
    }

    destroy() {
        this.clear();
    }
}
